/*

  ChallengeDialog.ts

  Challenge dialog component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, UserListItem } from "./types";
import { ComponentFunc, m } from "./mithril";
import { mt, t, ts } from "./i18n";
import { glyph, nbsp } from "./util";
import { DialogButton, MultiSelection, OnlinePresence } from "./components";

interface IAttributes {
  view: IView;
  item: UserListItem;
}

export const ChallengeDialog: ComponentFunc<IAttributes> = () => {
  // Show a dialog box for a new challenge being issued
  let manualChallenge = false;
  return {
    view: (vnode) => {
      const { view, item } = vnode.attrs;
      const model = view.model;
      const state = model.state;
      if (!state) return undefined;
      const manual = state.plan !== ""; // If subscriber/friend, allow manual challenges
      const fairPlay = item.fairplay && state.fairPlay; // Both users are fair-play
      return m(".modal-dialog",
        { id: 'chall-dialog', style: { visibility: 'visible' } },
        m(".ui-widget.ui-widget-content.ui-corner-all", { id: 'chall-form' },
          [
            m(".chall-hdr",
              m("table",
                m("tbody",
                  m("tr",
                    [
                      m("td", m("h1.chall-icon", glyph("hand-right"))),
                      m("td.l-border",
                        [
                          m(OnlinePresence, {
                            id: "chall-online",
                            userId: item.userid,
                            state,
                          }),
                          m("h1", item.nick),
                          m("h2", item.fullname)
                        ]
                      )
                    ]
                  )
                )
              )
            ),
            m("div", { style: { "text-align": "center" } },
              [
                m(".promo-fullscreen",
                  [
                    mt("p", [mt("strong", "Ný áskorun"), " - veldu lengd viðureignar:"]),
                    m(MultiSelection,
                      { initialSelection: 0, defaultClass: 'chall-time' },
                      [
                        m("div", { id: 'chall-none', tabindex: 1 },
                          t("Viðureign án klukku")
                        ),
                        m("div", { id: 'chall-10', tabindex: 2 },
                          [glyph("time"), t("2 x 10 mínútur")]
                        ),
                        m("div", { id: 'chall-15', tabindex: 3 },
                          [glyph("time"), t("2 x 15 mínútur")]
                        ),
                        m("div", { id: 'chall-20', tabindex: 4 },
                          [glyph("time"), t("2 x 20 mínútur")]
                        ),
                        m("div", { id: 'chall-25', tabindex: 5 },
                          [glyph("time"), t("2 x 25 mínútur")]
                        ),
                        state?.runningLocal ? // !!! TODO Debugging aid
                          m("div", { id: 'chall-3', tabindex: 6 },
                            [glyph("time"), t("2 x 3 mínútur")]
                          )
                        :
                          m("div", { id: 'chall-30', tabindex: 6 },
                            [glyph("time"), t("2 x 30 mínútur")]
                          )
                      ]
                    )
                  ]
                ),
                m(".promo-mobile",
                  [
                    m("p", mt("strong", "Ný áskorun")),
                    m(".chall-time.selected",
                      { id: 'extra-none', tabindex: 1 },
                      t("Viðureign án klukku")
                    )
                  ]
                )
              ]
            ),
            manual ? m("div", { id: "chall-manual" },
              [
                mt("span.caption.wide",
                  [
                    "Nota ", mt("strong", "handvirka véfengingu"),
                    m("br"), "(\"keppnishamur\")"
                  ]
                ),
                m(".toggler[id='manual-toggler'][tabindex='7']",
                  {
                    onclick: (ev: Event) => {
                      manualChallenge = !manualChallenge;
                      ev.preventDefault();
                    }
                  },
                  [
                    m(`.option${manualChallenge ? "" : ".selected"}`,
                      m("span", nbsp())
                    ),
                    m(`.option${manualChallenge ? ".selected" : ""}`,
                      glyph("lightbulb")
                    )
                  ]
                )
              ]
            ) : "",
            fairPlay ? m("div", { id: "chall-fairplay" },
              [
                t("Báðir leikmenn lýsa því yfir að þeir skrafla "),
                m("br"),
                mt("strong", "án stafrænna hjálpartækja"),
                t(" af nokkru tagi"), "."
              ]
            ) : "",
            m(DialogButton,
              {
                id: "chall-cancel",
                title: ts("Hætta við"),
                tabindex: 8,
                onclick: (ev: Event) => {
                  view.popDialog();
                  ev.preventDefault();
                }
              },
              glyph("remove")
            ),
            m(DialogButton,
              {
                id: "chall-ok",
                title: ts("Skora á"),
                tabindex: 9,
                onclick: (ev: Event) => {
                  // Issue a new challenge
                  let duration: string | number =
                    document.querySelector("div.chall-time.selected")?.id.slice(6) ?? "0";
                  if (duration === "none")
                    duration = 0;
                  else
                    duration = parseInt(duration);
                  item.chall = true;
                  view.actions.handleChallenge(
                    {
                      destuser: item.userid,
                      action: "issue",
                      duration: duration,
                      fairplay: fairPlay,
                      manual: manualChallenge
                    }
                  );
                  view.popDialog();
                  ev.preventDefault();
                }
              },
              glyph("ok")
            )
          ]
        )
      );
    }
  };
};
